import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ColorType,
  createChart,
  type IChartApi,
  type ISeriesApi,
  type UTCTimestamp,
} from "lightweight-charts";

import { equity } from "@/api/equity";
import { useActiveAccount } from "@/components/ActiveAccount";
import { useWsSubscribe } from "@/ws/WebSocketProvider";
import { EmptyState, Skeleton } from "@/components/ui";

/**
 * Daily equity curve for the active trading account. Reads
 * `/api/v1/equity-curve` and renders as a single line series.
 */
export function EquityCurvePane() {
  const { active } = useActiveAccount();
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Area"> | null>(null);

  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["equity-curve", active?.id],
    queryFn: () => equity.curve(active!.id),
    enabled: !!active,
    refetchInterval: 30_000,
  });

  // Refetch on every fill so the latest day stays current.
  useWsSubscribe("fill", () => {
    qc.invalidateQueries({ queryKey: ["equity-curve"] });
  });
  useWsSubscribe("internal_fill", () => {
    qc.invalidateQueries({ queryKey: ["equity-curve"] });
  });

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: "rgb(12, 16, 28)" },
        textColor: "rgb(148, 163, 184)",
        fontSize: 10,
      },
      grid: {
        vertLines: { color: "rgba(148, 163, 184, 0.05)" },
        horzLines: { color: "rgba(148, 163, 184, 0.05)" },
      },
      timeScale: { timeVisible: true, secondsVisible: false },
      rightPriceScale: { borderColor: "rgba(148, 163, 184, 0.1)" },
    });
    chartRef.current = chart;
    seriesRef.current = chart.addAreaSeries({
      lineColor: "rgb(56, 189, 248)",
      topColor: "rgba(56, 189, 248, 0.3)",
      bottomColor: "rgba(56, 189, 248, 0.0)",
      lineWidth: 2,
    });
    const ro = new ResizeObserver(() => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        });
      }
    });
    ro.observe(containerRef.current);
    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!seriesRef.current || !q.data) return;
    const data = q.data.points.map((p) => ({
      time: p.time as UTCTimestamp,
      value: p.value,
    }));
    seriesRef.current.setData(data);
    chartRef.current?.timeScale().fitContent();
  }, [q.data]);

  if (!active) {
    return <EmptyState title="No active account" description="Pick a trading account to view its equity curve." />;
  }
  if (q.isLoading) {
    return (
      <div className="space-y-1.5 p-2">
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (!q.data || q.data.points.length === 0) {
    return <EmptyState title="No equity yet" description="Once your account has activity, the curve will populate here." />;
  }

  return <div ref={containerRef} className="h-full w-full" />;
}
